import { useMemo } from 'react'
import { DataTable, PaginationWrapper } from '@/components/organisms'
import { UnlockFilters } from '@/components/filters'
import { TableSkeleton } from '@/components/ui/table-skeleton'
import { formatDate, formatCompactNumber } from '@/utils'
import { ProjectUnlock, UnlocksQueryParams } from '@/types/unlocks'

interface UnlocksTableProps {
  title: string
  navigate: (path: string) => void
  useDataHook: (params: Record<string, any>) => any
  dataKey: string
  showPagination?: boolean
  useUrlParams?: boolean
  search?: string
  sortBy?: UnlocksQueryParams['sort_by']
  sortDirection?: UnlocksQueryParams['sort_direction']
  onSearchChange?: (search: string) => void
  onSortByChange?: (sortBy: UnlocksQueryParams['sort_by']) => void
  onSortDirectionChange?: (sortDirection: UnlocksQueryParams['sort_direction']) => void
}

const UnlocksTable = ({ 
  title, 
  navigate, 
  useDataHook, 
  dataKey: _dataKey, 
  showPagination = true,
  useUrlParams: _useUrlParams = false,
  search = '',
  sortBy = 'VESTING_DATE_START',
  sortDirection = 'DESC',
  onSearchChange,
  onSortByChange,
  onSortDirectionChange
}: UnlocksTableProps) => {
  // Memoize params to prevent infinite re-renders
  const params: UnlocksQueryParams = useMemo(() => ({
    search: search || undefined,
    sort_by: (sortBy || 'VESTING_DATE_START') as UnlocksQueryParams['sort_by'],
    sort_direction: (sortDirection || 'DESC') as UnlocksQueryParams['sort_direction'],
    limit: 10
  }), [search, sortBy, sortDirection])

  const dataHookResult = useDataHook(params)
  
  // Get data from useUnlocks hook
  const {
    unlocks,
    loading,
    total,
    currentPage,
    totalPages,
    goToPage
  } = dataHookResult

  const handleProjectClick = (unlock: ProjectUnlock) => {
    navigate(`/data-rooms/${unlock.project_id}`)
  }

  const columns = [
    {
      key: 'project_name',
      title: 'Project',
      width: 'w-[322px]',
      render: (item: ProjectUnlock) => (
        <div className="flex items-center gap-[10px]">
          <div className="bg-[var(--base-muted,#f5f5f5)] dark:bg-[#2a2a2a] overflow-hidden rounded-full shrink-0 size-[26px]">
            {item.logo ? (
              <img
                src={item.logo}
                alt={item.project_name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-full h-full bg-gray-200 dark:bg-gray-700" />
            )}
          </div>
          <div className="font-['Plus_Jakarta_Sans'] font-medium text-[#2d2923] dark:text-[#fdfdfd] text-[14px] leading-[20px] overflow-hidden text-ellipsis whitespace-nowrap">
            {item.project_name}
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'unlock_type',
      title: 'Type',
      width: 'w-[180px]',
      render: (item: ProjectUnlock) => {
        const type = (item.unlock_type || 'token').toLowerCase()
        const isToken = type === 'token'
        return (
          <div 
            className={`
              bg-[rgba(255,255,255,0.4)] dark:bg-[rgba(255,255,255,0.1)]
              border border-solid
              ${isToken ? 'border-[#9db807]' : 'border-[#6b6969]'}
              flex items-center justify-center
              px-[24px] py-[2px]
              w-[92px] h-[24px]
            `}
          >
            <span 
              className={`
                font-['Plus_Jakarta_Sans'] font-medium leading-[16px] text-[12px] uppercase
                ${isToken ? 'text-[#9db807]' : 'text-[#575757] dark:text-[#a0a0a0]'}
              `}
            >
              {type}
            </span>
          </div>
        )
      }
    },
    {
      key: 'circulating_supply',
      title: 'Circulating Supply',
      width: 'w-[168px]',
      render: (item: ProjectUnlock) => (
        <span className="font-['Plus_Jakarta_Sans'] font-normal text-[#2d2923] dark:text-[#fdfdfd] text-[14px] leading-[20px]">
          {item.circulating_supply != null ? `$${formatCompactNumber(item.circulating_supply)}` : '－'}
        </span>
      )
    },
    {
      key: 'total_locked',
      title: 'Total Locked',
      width: 'w-[168px]',
      render: (item: ProjectUnlock) => (
        <span className="font-['Plus_Jakarta_Sans'] font-normal text-[#2d2923] dark:text-[#fdfdfd] text-[14px] leading-[20px]">
          {item.total_locked != null ? `$${formatCompactNumber(item.total_locked)}` : '－'}
        </span>
      ),
      sortable: true
    },
    {
      key: 'total_unlocked',
      title: 'Total Unlocked',
      width: 'w-[168px]',
      render: (item: ProjectUnlock) => (
        <span className="font-['Plus_Jakarta_Sans'] font-normal text-[#2d2923] dark:text-[#fdfdfd] text-[14px] leading-[20px]">
          {item.total_unlocked != null ? `$${formatCompactNumber(item.total_unlocked)}` : '－'}
        </span>
      )
    },
    {
      key: 'vesting_start_date',
      title: 'Vesting Start Date',
      width: 'w-[168px]',
      render: (item: ProjectUnlock) => (
        <span className="font-['Plus_Jakarta_Sans'] font-normal text-[#2d2923] dark:text-[#fdfdfd] text-[14px] leading-[20px]">
          {item.vesting_start_date ? formatDate(item.vesting_start_date) : '－'}
        </span>
      ),
      sortable: true
    },
    {
      key: 'vesting_end_date',
      title: 'Vesting End Date',
      width: 'w-[168px]',
      render: (item: ProjectUnlock) => (
        <span className="font-['Plus_Jakarta_Sans'] font-normal text-[#2d2923] dark:text-[#fdfdfd] text-[14px] leading-[20px]">
          {item.vesting_end_date ? formatDate(item.vesting_end_date) : '－'}
        </span>
      )
    },
    {
      key: 'listing_date',
      title: 'Listing Date',
      width: 'w-[168px]',
      render: (item: ProjectUnlock) => (
        <span className="font-['Plus_Jakarta_Sans'] font-normal text-[#2d2923] dark:text-[#fdfdfd] text-[14px] leading-[20px]">
          {item.listing_date ? formatDate(item.listing_date) : '－'}
        </span>
      )
    }
  ]

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div className="flex items-center justify-between h-[52px]">
        <h3 className="font-['Plus_Jakarta_Sans'] font-semibold leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] text-[23px] tracking-[-0.2px]">
          {title}
        </h3>
      </div>

      {/* Table Container with Figma styling */}
      <div className="bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(28,28,28,0.32)] border border-white dark:border-[rgba(255,255,255,0.1)] rounded-none overflow-hidden">
        {/* Filters */}
        <div className="bg-[rgba(255,255,255,0.44)] dark:bg-[rgba(28,28,28,0.44)] px-[8px] py-[8px]">
          <UnlockFilters
            search={search}
            onSearchChange={onSearchChange || (() => {})}
            sortBy={sortBy}
            onSortByChange={onSortByChange || (() => {})}
            sortDirection={sortDirection}
            onSortDirectionChange={onSortDirectionChange || (() => {})}
          />
        </div>

        {/* Table */}
        <div className="bg-[rgba(255,255,255,0.2)] dark:bg-[rgba(28,28,28,0.2)] overflow-clip">
          {loading ? (
            <TableSkeleton 
              variant="elevated"
              rows={5}
              columns={8}
              showHeader={false}
              showFilters={false}
            />
          ) : (
            <DataTable
              columns={columns}
              data={unlocks || []}
              keyExtractor={(item) => item.project_id}
              isLoading={false}
              onRowClick={handleProjectClick}
            />
          )}
        </div>
      </div>
      
      {/* Pagination */}
      {showPagination && totalPages && totalPages > 1 && (
        <div className="flex justify-center">
          <PaginationWrapper
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />
        </div>
      )}
      
      {/* Information about number of records */}
      <div className="text-center text-sm text-[var(--muted-foreground)]">
        Showing {unlocks?.length || 0} of {total} token unlocks
      </div>
    </div>
  )
}

export default UnlocksTable 